// PagePersonalEdit.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Button, Typography, Alert, Snackbar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormEdit from './EditPersonalForm';
import ConfirmationCancelEditDialog from './dialog/ConfirmationCancelEditDialog';
import ActivateDialog from './dialog/ActivateDialog';
import CalendarSyncProgressOverlay from '../dialogs/CalendarSyncProgressOverlay';
import api from '../../../config/axiosInstance';

const PagePersonalEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employeeData } = location.state || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchEditData = useCallback(async () => {
    if (!employeeData?.ci) return;
    try {
      const res = await api.get(`personal/edit_info?ci=${encodeURIComponent(employeeData.ci)}`);
      const payload = res.data;

      // El backend puede devolver { data: [...], primary_workarea, aux_workareas_fixed, ... }
      // Tomamos el primer bloque de "data"
      const base = Array.isArray(payload?.data) ? payload.data[0] : payload[0] || payload;

      setData({
        left: base?.left || null,
        right: base?.right || null,
        primaryWorkareaId: payload?.primary_workarea ?? null,
        auxFixedIds: payload?.aux_workareas_fixed ?? [],
        auxValidFrom: payload?.aux_valid_from ?? null,
        availableIds: payload?.available_workareas ?? [], // por si querés usarlo más adelante
        weeklyScheduleSupported: Boolean(payload?.weekly_schedule_supported),
        weeklySchedule: payload?.weekly_schedule ?? [],
        workshiftsByStation: payload?.workshifts_by_station ?? {},
        rotationWorkshiftSupported: Boolean(payload?.rotation_workshift_supported),
        rotationWorkshiftIds: payload?.rotation_workshift_ids ?? [],
        employmentPeriods: payload?.employment_periods ?? [],
      });
    } catch (error) {
      console.error('Error fetching edit data:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar datos del empleado',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [employeeData?.ci]);

  useEffect(() => {
    if (!employeeData?.ci) {
      // Se llegó a esta ruta sin el estado de navegación (ej. recarga del
      // navegador en esta URL) -- no hay CI para consultar, volvemos al listado.
      navigate('/personal_table', { replace: true });
      return;
    }
    fetchEditData();
  }, [fetchEditData, employeeData, navigate]);

  const handleActivateEmployee = async (startDate) => {
    setActivating(true);
    try {
      const params = new URLSearchParams({
        ci: employeeData.ci,
        start_date: startDate,
      });
      const response = await api.put(`personal/edit_activate?${params.toString()}`);
      const updatedCalendars = Number(response?.data?.calendar_sync?.updated_calendars || 0);
      setSnackbar({
        open: true,
        message: updatedCalendars
          ? `Empleado reactivado. Se actualizaron ${updatedCalendars} calendario(s) abierto(s).`
          : 'Empleado reactivado exitosamente',
        severity: 'success'
      });
      setActivateDialogOpen(false);
      // Recargar los datos para reflejar el cambio
      await fetchEditData();
    } catch (error) {
      console.error('Error activating employee:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error al activar empleado',
        severity: 'error'
      });
    } finally {
      setActivating(false);
    }
  };

  const handleBackClick = () => {
    if (isFormDirty) {
      setConfirmDialogOpen(true);
    } else {
      navigate('/personal_table', { replace: true });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isEmployeeInactive =
    data?.left?.data_form?.[0]?.enabled?.value === 0 ||
    data?.left?.data_form?.[0]?.enabled?.value === 'INACTIVO' ||
    data?.left?.data_form?.[0]?.enabled?.value === '0';

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleBackClick}
          sx={{ mr: 2 }}
          startIcon={<ArrowBackIcon />}
        >
          Atrás
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1, textAlign: 'center' }}>
          Editar datos del empleado
        </Typography>
      </Box>

      {isEmployeeInactive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Este empleado está INACTIVO. Para poder editarlo y asignarle licencias, debe activarlo primero.
        </Alert>
      )}

      <FormEdit
        employeeData={{
          ...(data.left || {}),
          weekly_schedule_supported: data.weeklyScheduleSupported,
          weekly_schedule: data.weeklySchedule,
          workshifts_by_station: data.workshiftsByStation,
          rotation_workshift_supported: data.rotationWorkshiftSupported,
        rotation_workshift_ids: data.rotationWorkshiftIds,
        employment_periods: data.employmentPeriods,
        }}
        primaryWorkareaId={data.primaryWorkareaId}
        auxFixedIds={data.auxFixedIds}
        auxValidFrom={data.auxValidFrom}
        isFormDirty={isFormDirty}
        setIsFormDirty={setIsFormDirty}
        isEmployeeInactive={isEmployeeInactive}
        onActivateEmployee={() => setActivateDialogOpen(true)}
        activating={activating}
        onRefresh={fetchEditData}
        licenses={data.right?.licenses}
        history={data.right?.history}
      />

      <ConfirmationCancelEditDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => navigate('/personal_table', { replace: true })}
      />

      <ActivateDialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
        onConfirm={handleActivateEmployee}
      />

      <CalendarSyncProgressOverlay open={activating} variant="activate" />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PagePersonalEdit;
